<template>
  <BasePopper
    v-model="visible"
    caret
    class="c-social-amplify tw:text-left"
    click-outside-capture
    :fallback-placements="[
      'left-end',
      'left-start',
      'right-end',
      'right-start',
    ]"
    flip
    :offset="12"
    placement="right-start"
    :root-element="rootElement"
    tether
  >
    <template #arrow="{ styles, placement: floatingPlacement, setArrowRef }">
      <div
        :ref="(el) => setArrowRef(el)"
        class="c-social-amplify__arrow"
        :data-placement="floatingPlacement"
        :style="styles"
      />
    </template>
    <div
      class="c-social-amplify__container q-pa-md"
      @click.stop.prevent
    >
      <div class="c-social-amplify__title">
        <h5>{{ $t("social_amplify_popup.social_amplify") }}</h5>

        <BaseButton
          color="white"
          flat
          icon="ion-close"
          icon-size="24px"
          padding="none sm"
          @click="visible = false"
        />
      </div>

      <div
        v-if="loading"
        class="q-pa-md"
      >
        <QSpinner
          class="c-social-amplify__spinner"
          color="white"
          :size="20"
        />
      </div>
      <template v-else>
        <div class="c-social-amplify__total">
          <p>
            {{
              $t("social_amplify_popup.total_interactions", {
                total: total.toLocaleString(),
              })
            }}
          </p>
        </div>

        <div
          v-if="hasAmplifyData"
          class="c-social-amplify__widget"
        >
          <h6>
            {{ $t("social_amplify_popup.trend_with_date", { dateRangeLabel }) }}
          </h6>
          <p>
            {{
              $t("social_amplify_popup.realtime_social_amplification_subtext")
            }}
          </p>

          <div class="chart-container">
            <Chart
              class="q-mt-md c-social-amplify__widget--trend"
              :container-style="{ width: '350px', height: '160px' }"
              :data="trendProps.data"
              :options="trendProps.options"
              type="line"
            />
          </div>
        </div>

        <div class="c-social-amplify__widget">
          <h6>
            {{ $t("social_amplify_popup.amplification_by_platform_title") }}
          </h6>
          <p>
            {{ $t("social_amplify_popup.amplification_by_platform_subtext") }}
          </p>

          <div class="chart-container">
            <Chart
              class="q-mt-sm c-social-amplify__widget--amplification"
              :container-style="{ width: '145px', height: '145px' }"
              :data="amplificationProps.data"
              :options="amplificationProps.options"
              type="polarArea"
            />

            <Legend
              class="q-ml-sm"
              :items="amplificationProps.legend"
              layout="column"
            />
          </div>
        </div>
      </template>
    </div>
  </BasePopper>
</template>

<script>
import { storeToRefs } from "pinia";

import { BaseButton, BasePopper } from "shared/components/base";
import useSocialAmplify from "shared/composables/useSocialAmplify";
import {
  dateInTimeZone,
  shortTimezone,
  toMilliseconds,
} from "shared/helpers/date";
import { useGlobalStore } from "shared/stores/global";

export default {
  name: "SocialAmplifyPopup",
  components: {
    BaseButton,
    BasePopper,
  },
  props: {
    modelValue: {
      type: Boolean,
      required: true,
    },
    mention: {
      type: Object,
      required: true,
    },
    total: {
      type: Number,
      required: true,
    },
    rootElement: {
      type: HTMLElement,
      default: null,
    },
  },
  emits: ["update:modelValue"],
  setup(props) {
    const {
      hasAmplifyData,
      interval,
      loading,
      range,
      socialAmplifyData,
      loadSocialAmplifyData,
    } = useSocialAmplify(props);

    const globalStore = useGlobalStore();
    const { isCisionOne } = storeToRefs(globalStore);

    return {
      hasAmplifyData,
      interval,
      isCisionOne,
      loading,
      range,
      loadSocialAmplifyData,
      socialAmplifyData,
    };
  },
  data() {
    return {
      colors: {
        Twitter: {
          backgroundColor: "rgba(101, 180, 236, 0.8)",
          legend: "#69C0FF",
        },
        Facebook: {
          backgroundColor: "rgba(47, 84, 235, 0.8)",
          legend: "#2F54EB",
        },
        Reddit: {
          backgroundColor: "rgba(255, 111, 69, 0.8)",
          legend: "#FF6F45",
        },
        Pinterest: {
          backgroundColor: "rgba(230, 0, 35, 0.8)",
          legend: "#E60023",
        },
      },
    };
  },
  computed: {
    visible: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit("update:modelValue", value);
      },
    },
    beforeFormatted() {
      return dateInTimeZone(this.range.before, "d MMM", this.timezone);
    },
    afterFormatted() {
      return dateInTimeZone(this.range.after, "d MMM", this.timezone);
    },
    timezone() {
      return this.mention.source.time_zone;
    },
    timezoneFormatted() {
      return shortTimezone(new Date(), this.mention.source.time_zone);
    },
    dateRangeLabel() {
      const after = new Date(toMilliseconds(this.range.after));
      const before = new Date(toMilliseconds(this.range.before));

      if (after.getDate() === before.getDate()) {
        return `${this.beforeFormatted}, ${this.timezoneFormatted}`;
      }

      if (after.getMonth() === before.getMonth()) {
        return `${after.getDate()} - ${this.beforeFormatted}, ${
          this.timezoneFormatted
        }`;
      }

      return `${this.afterFormatted} - ${this.beforeFormatted}, ${this.timezoneFormatted}`;
    },
    dateFormatter() {
      const { timezone, interval } = this;

      const format = {
        "15m": "h:mmaaa",
        "1h": "haaa",
        "1d": "d LLL",
      }[interval];

      return function formatTicks(value) {
        const date = new Date(value);
        let dateFormat = format;

        if (date.getHours() === 0 && interval !== "1d") {
          dateFormat = `d LLL - ${dateFormat}`;
        }

        return dateInTimeZone(new Date(date), dateFormat, timezone);
      };
    },
    trendProps() {
      const { dateFormatter, tooltipDateFormatter } = this;

      const labels = this.socialAmplifyData[0][2].buckets.map(
        (bucket) => new Date(bucket.key_as_string)
      );

      const data = new Array(labels.length).fill(0);

      this.socialAmplifyData.forEach((dataSet) => {
        dataSet[2].buckets.forEach((bucket, index) => {
          data[index] += bucket.total_engagement.value;
        });
      });

      const chartColour = this.isCisionOne ? "#fff" : "#D3ADF7";

      return {
        data: {
          labels,
          datasets: [
            {
              data,
              backgroundColor: ["#fff"],
              borderWidth: "5",
              borderColor: ["#fff"],
              radius: 0,
              pointHitRadius: 0,
              fill: false,
            },
          ],
        },
        options: {
          layout: {
            padding: 8,
          },
          scales: {
            x: {
              ticks: {
                color: chartColour,
                callback(value) {
                  return dateFormatter(this.getLabelForValue(value));
                },
              },
              grid: {
                zeroLineColor: chartColour,
                color: chartColour,
              },
            },
            y: {
              ticks: {
                color: chartColour,
              },
              grid: {
                zeroLineColor: chartColour,
                color: chartColour,
              },
            },
          },
          plugins: {
            clickevents: false,
            tooltip: {
              displayColors: false,
              callbacks: {
                title(tooltipItem) {
                  return tooltipDateFormatter(
                    dateFormatter(tooltipItem[0].label),
                    tooltipItem[0].raw
                  );
                },
              },
            },
          },
        },
      };
    },
    relevantPlatforms() {
      return ["Twitter", "Facebook", "Reddit", "Pinterest"];
    },
    amplificationData() {
      const data = [];
      const backgroundColor = [];
      const legend = [];

      this.relevantPlatforms.forEach((platform) => {
        const count = this.mention[`${platform.toLowerCase()}_shares`] || 0;
        data.push(count);
        backgroundColor.push(this.colors[platform].backgroundColor);

        legend.push({
          color: this.colors[platform].legend,
          label: `${platform}: ${count} (${this.percentage(
            count,
            this.totalCount
          )}%)`,
        });
      });

      return {
        data,
        backgroundColor,
        legend,
      };
    },
    amplificationProps() {
      const { data, backgroundColor, legend } = this.amplificationData;

      const chartColour = this.isCisionOne ? "#fff" : "#D3ADF7";

      return {
        data: {
          datasets: [
            {
              data,
              backgroundColor,
              borderWidth: 0,
            },
          ],
          labels: this.relevantPlatforms,
        },
        options: {
          maintainAspectRatio: true,
          responsive: true,
          layout: {
            padding: 4,
          },
          plugins: {
            clickevents: false,
            tooltip: {
              enabled: false,
              external: null,
            },
          },
          scales: {
            r: {
              grid: {
                color: chartColour,
              },
              ticks: {
                display: false,
              },
            },
          },
        },
        legend,
      };
    },
    totalCount() {
      return this.mention.total_social_shares;
    },
  },
  watch: {
    modelValue: "loadData",
  },
  methods: {
    loadData() {
      if (this.modelValue) {
        this.loadSocialAmplifyData();
      }
    },
    tooltipDateFormatter(date, value) {
      return `${date}: ${value}`;
    },
    percentage(point, total) {
      return Math.round((point / total || 0) * 100);
    },
  },
};
</script>

<style lang="scss" scoped>
.c-social-amplify {
  &__container {
    background: var(--s-amplify-bg);
    border-radius: 6px;
    width: 350px;
    z-index: 99999;
  }

  &__title {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;

    h5 {
      font-family: $typography-font-family;
      font-style: normal;
      font-weight: bold;
      font-size: 19px;
      line-height: 100%;
      color: $white;
      margin: 0;
    }
  }

  &__total {
    margin: 8px 0 0;

    p {
      font-family: $typography-font-family;
      font-style: normal;
      font-weight: normal;
      font-size: 14px;
      line-height: 120%;
      color: $white;
      margin: 0;
    }
  }

  &__widget {
    margin: 20px 0;

    h6 {
      font-family: $typography-font-family;
      font-style: normal;
      font-weight: bold;
      font-size: 16px;
      line-height: 100%;
      color: $white;
      margin: 0 0 8px;
    }

    p {
      font-family: $typography-font-family;
      font-style: normal;
      font-weight: normal;
      font-size: 14px;
      line-height: 120%;
      color: $white;
      margin: 0;
    }

    .chart-container {
      display: flex;
      flex-direction: row;

      :deep(.c-legend-item__label) {
        color: $white;
      }
    }

    &--amplification {
      overflow: hidden;
    }
  }

  &__arrow {
    position: absolute;
    background-color: var(--s-amplify-bg);
    transform: rotate(-60deg) skewX(-30deg) scale(1, 0.866) !important;

    &::before,
    &::after {
      content: "";
      position: absolute;
      background-color: inherit;
    }

    &,
    &::before,
    &::after {
      width: 18px;
      height: 18px;
      border-top-right-radius: 30%;
    }

    &::before {
      transform: rotate(-135deg) skewX(-45deg) scale(1.414, 0.707)
        translate(0, -50%) !important;
    }

    &::after {
      transform: rotate(135deg) skewY(-45deg) scale(0.707, 1.414) translate(50%) !important;
    }

    &[data-placement^="left"] {
      right: -12px;
      transform: rotate(-90deg) skewX(-30deg) scale(1, 0.866) !important;
    }

    &[data-placement^="right"] {
      left: -12px;
      transform: rotate(-30deg) skewX(-30deg) scale(1, 0.866) !important;
    }
  }

  &__spinner {
    position: relative;
    top: 45%;
    left: 45%;
  }
}
</style>
